import type { LedgerEntryRow } from "@/modules/wallet/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { CompactNumber } from "@/shared/ui/CompactNumber";
import { DataTable, createSortableColumn, type TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";


import {
  ArrowDownIcon,
  ArrowUpIcon,
  CreditCardIcon,
  ScaleIcon,
} from "lucide-react";

const TYPE_LABELS: Record<LedgerEntryRow["type"], string> = {
  TOPUP: "ເຕີມເງິນ",
  PAYMENT: "ຊຳລະ",
  REFUND: "ຄືນເງິນ",
  ADJUSTMENT: "ປັບຍອດ",
};

const TYPE_ICONS: Record<LedgerEntryRow["type"], React.ReactNode> = {
  TOPUP: <ArrowUpIcon className="h-4 w-4 text-emerald-600" />,
  PAYMENT: <CreditCardIcon className="h-4 w-4 text-blue-600" />,
  REFUND: <ArrowDownIcon className="h-4 w-4 text-rose-600" />,
  ADJUSTMENT: <ScaleIcon className="h-4 w-4 text-slate-600" />,
};

const DIRECTION_LABELS: Record<LedgerEntryRow["direction"], string> = {
  CREDIT: "ເຂົ້າ",
  DEBIT: "ອອກ",
};

type LedgerTableProps = {
  data: LedgerEntryRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function LedgerTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
}: LedgerTableProps) {
  const columns: TanstackReactTable.ColumnDef<LedgerEntryRow>[] = [
    createSortableColumn<LedgerEntryRow>("createdAt", "ວັນທີ", {
      size: 160,
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
    }),
    {
      accessorKey: "type",
      header: "ປະເພດ",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {TYPE_ICONS[row.original.type]}
          <span className="text-sm">{TYPE_LABELS[row.original.type]}</span>
        </div>
      ),
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "direction",
      header: "ທິດ",
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.direction === "CREDIT" ? "success" : "destructive"
          }
        >
          {DIRECTION_LABELS[row.original.direction]}
        </Badge>
      ),
      size: 80,
      enableSorting: false,
    },
    {
      accessorKey: "amountLak",
      header: "ຍອດ (LAK)",
      cell: ({ row }) => (
        <span
          className={`text-sm ${row.original.direction === "CREDIT" ? "text-emerald-600" : "text-rose-600"}`}
        >
          <CompactNumber
            value={Number(row.original.amountLak)}
            prefix={row.original.direction === "CREDIT" ? "+" : "-"}
            formatOptions={{
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }}
          />
        </span>
      ),
      size: 130,
      enableSorting: false,
    },
    {
      accessorKey: "balanceAfterLak",
      header: "ຍອດຄົງເຫຼືອ (LAK)",
      cell: ({ row }) => (
        <CompactNumber
          value={Number(row.original.balanceAfterLak)}
          className="text-sm"
          formatOptions={{
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }}
        />
      ),
      size: 140,
      enableSorting: false,
    },
    {
      accessorKey: "orderNo",
      header: "ອໍເດີ",
      cell: ({ row }) => (
        <span className="font-mono text-muted-foreground text-sm">
          {row.original.orderNo ?? "–"}
        </span>
      ),
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "customerName",
      header: "ລູກຄ້າ",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.original.customerName ?? "–"}
          </span>
          <span className="text-muted-foreground text-xs">
            {row.original.customerPhone}
          </span>
        </div>
      ),
      size: 160,
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.status}
        </span>
      ),
      size: 100,
      enableSorting: false,
    },
  ];

  return (
    <DataTable<LedgerEntryRow, unknown>
      noDataMessage="ບໍ່ພົບລາຍການໃນ ledger"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
